import React, { useCallback, useMemo, ReactElement } from 'react';
import Tooltip from '../../../../components/tooltip/tooltip.component';
import CheckAccess from '../../../../components/check-access/check-access.component';
import TableRowForm from '../../../../components/table-row-form/table-row-form.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import TextBlock from '../../../../components/text-block/text-block.component';
import Loader from '../../../../components/loader/loader.component';
import ActionButton from '../../../../components/action-button/action-button.component';
import IconBlockError from '../../../../components/icon-block-error/icon-block-error.component';
import IconBlockSuccess from '../../../../components/icon-block-success/icon-block-success.component';
import FormFieldSelect from '../../../../components/form-field-select/form-field-select.component';
import FormFieldText from '../../../../components/form-field-text/form-field-text.component';
import FormFieldUpload from '../../../../components/form-field-upload/form-field-upload.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import AnalyzeIcon from '../../../../icons/analyze-icon';
import RemoveIcon from '../../../../icons/remove-icon';
import ArrowLinkIcon from '../../../../icons/arrow-link-icon';
import FileService from '../../../../services/file-service';
import FormService from '../../../../services/form-service';
import useShiftState from '../../../../hooks/use-shift-state';
import { DEAL_FILE_ACCESS_LEVEL_VIEW } from '../../../../constants/entity/permissions';
import { FILE_EXTENSION_LIST_ANALYZABLE } from '../../../../constants/system/file-extensions';

const DealFileRowForm: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const FILE_PREVIEW_NAME_LIMIT: number = 15;

  const {
    isActive,
    isSaving,
    isSaveDisabled,
    isInlineSaving,
    isSimple,
    isNewDeal,
    isNewEntity,
    isDealFileAnalyzed,
    dealFileIndex,
    dealFileData,
    dealFileGroupData,
    dealFileFile,
    fileDropDeps,
    inputRefs,
    accessLevelList,
    onFileChange,
    onFileDelete,
    onOpenModal,
    onDeleteClick,
    onInlineSave,
    onInlineCancel,
    onDealFileAnalyzeClick,
  } = props;

  const isShiftPressed: boolean = useShiftState();

  const dealFileAccessLevelViewPermissions: string[] = useMemo((): string[] => [DEAL_FILE_ACCESS_LEVEL_VIEW], []);

  const dealFileId: number = (dealFileData && dealFileData.id) || FormService.FORM_KEY_NEW;
  const dealFileGroupId: number = (dealFileGroupData && dealFileGroupData.id) || FormService.FORM_KEY_NEW;
  const formKeyDealFile: string = FormService.getIndexedFormFieldKey(dealFileId);
  const formKeyDealFileGroup: string = FormService.getIndexedFormFieldKey(dealFileGroupId);
  const formPrefix: string = `dealData.dealFilesData[${formKeyDealFileGroup}].files[${formKeyDealFile}]`;
  const subFieldIndexName: number = 1;
  const subFieldIndexFile: number = 2;
  const subFieldIndexAccessLevel: number = 3;

  const menuItemAccessLevelList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: accessLevelList,
  }), [accessLevelList]);

  const onDealFileFileChange: Function = useCallback((file: File): void => {
    if (onFileChange) {
      onFileChange(file, dealFileGroupId, dealFileId);
    }
  }, [dealFileGroupId, dealFileId, dealFileFile]);

  const onDealFileFileDelete: Function = useCallback((): void => {
    if (onFileDelete) {
      onFileDelete(dealFileGroupId, dealFileId);
    }
  }, [dealFileGroupId, dealFileId, dealFileFile]);

  return (
    <TableRowForm withNoBorders isActive={isActive}>
      <TableCell>
        <FormFieldText isHidden name={`${formPrefix}.id`} />
        <FormFieldText isHidden name={`${formPrefix}.groupId`} />
        <FormFieldText
          name={`${formPrefix}.name`}
          placeholder="Name"
          inputRefs={inputRefs}
          inputRefIndex={`${dealFileIndex}.${subFieldIndexName}`}
        />
      </TableCell>
      <TableCell>
        {
          isNewEntity && (
            <FormFieldUpload
              isInline
              withAllFilesAllowed
              isDroppable={!isSimple}
              uploadFile={dealFileFile}
              previewNameLimit={FILE_PREVIEW_NAME_LIMIT}
              name={`${formPrefix}.file`}
              buttonLabel="Upload New File"
              dropDeps={fileDropDeps}
              inputRefs={inputRefs}
              inputRefIndex={`${dealFileIndex}.${subFieldIndexFile}`}
              onFileChange={onDealFileFileChange}
              onFileDelete={onDealFileFileDelete}
            />
          )
        }
      </TableCell>
      {
        !isSimple && (
          <TableCell isDisabled>
            {
              dealFileData.file && (
                <TextBlock>
                  {`Date: ${dealFileData.file.date}`}
                </TextBlock>
              )
            }
          </TableCell>
        )
      }
      {
        !isSimple && (
          <TableCell isDisabled width="20%">
            {
              dealFileData.user && (
                <TextBlock>
                  {`By ${dealFileData.user.fullName}`}
                </TextBlock>
              )
            }
          </TableCell>
        )
      }
      {
        !isSimple && (
          <TableCell>
            <CheckAccess permissions={dealFileAccessLevelViewPermissions}>
              <FormFieldSelect
                withBlankElement
                isDisabled={accessLevelList && !accessLevelList.length}
                isReadOnly={isShiftPressed}
                blankElementLabel="Everyone"
                name={`${formPrefix}.accessLevel`}
                optionsTotal={accessLevelList && accessLevelList.length}
                inputRefs={inputRefs}
                inputRefIndex={!accessLevelList || !accessLevelList.length
                  ? `${dealFileIndex}.${subFieldIndexAccessLevel}` : null}
              >
                {menuItemAccessLevelList}
              </FormFieldSelect>
            </CheckAccess>
          </TableCell>
        )
      }
      <TableCell isActions isTextRight isSticky={!isSimple} isStickyRight={!isSimple} width="5%">
        {
          !isSimple && !isNewEntity && isSaving && isInlineSaving && (
            <Loader isInline isSmall />
          )
        }
        {
          !isSimple && !isNewEntity && !isSaveDisabled && !isSaving && (
            <ActionButton
              withSmallPadding
              onClick={(): void => {
                onInlineSave(dealFileGroupData.id, dealFileData.id);
              }}
            >
              <IconBlockSuccess />
            </ActionButton>
          )
        }
        {
          !isSimple && !isNewEntity && !isSaving && (
            <ActionButton
              withSmallPadding
              onClick={(): void => {
                onInlineCancel(dealFileGroupData.id, dealFileData.id);
              }}
            >
              <IconBlockError isDisabled={isSaveDisabled} />
            </ActionButton>
          )
        }
        {
          isNewDeal && !isDealFileAnalyzed && !!dealFileFile
          && FileService.checkFileExtension(dealFileFile, FILE_EXTENSION_LIST_ANALYZABLE) && (
            <Tooltip title="Analyze file content">
              <ActionButton isInline withSmallPadding onClick={onDealFileAnalyzeClick}>
                <AnalyzeIcon />
              </ActionButton>
            </Tooltip>
          )
        }
        {
          isNewEntity && !isSaving && (
            <Tooltip title="Delete">
              <ActionButton isInline withSmallPadding onClick={onDeleteClick}>
                <RemoveIcon />
              </ActionButton>
            </Tooltip>
          )
        }
        {
          isSimple && !isSaving && (
            <Tooltip title="Open in modal">
              <ActionButton isInline withSmallPadding onClick={onOpenModal}>
                <ArrowLinkIcon />
              </ActionButton>
            </Tooltip>
          )
        }
      </TableCell>
    </TableRowForm>
  );
});

export default DealFileRowForm;
